import React, { useRef } from 'react';
import { FileLock, FileWarning, FileX, Trash2, RotateCcw } from 'lucide-react';

const ACCEPTED_EXTENSIONS = '.pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.eml,.msg,.txt,.rtf';

const ReplaceButton = ({ onReplace }) => {
  const inputRef = useRef(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_EXTENSIONS}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onReplace(file);
          e.target.value = '';
        }}
      />
      <button className="btn-primary flex items-center gap-2" onClick={() => inputRef.current?.click()}>
        <RotateCcw size={14} />
        Replace
      </button>
    </>
  );
};

const ErrorCard = ({ icon: Icon, title, children, onReplace, onDelete }) => (
  <div className="bg-app-white border border-app-gray-900 rounded-app-lg pl-app-3xl py-app-2xl w-full flex flex-row">
    <div className="flex-[2] flex flex-col">
      <h3 className="text-app-xl font-semibold text-app-gray-900 mb-app-lg">{title}</h3>
      <div className="text-app-sm text-app-gray-700 flex-1">{children}</div>
      <div className="flex gap-app-2xl mt-app-lg">
        {onReplace && <ReplaceButton onReplace={onReplace} />}
        {onDelete && (
          <button className="btn-secondary flex items-center gap-2" onClick={onDelete}>
            <Trash2 size={14} />
            Delete
          </button>
        )}
      </div>
    </div>
    <div className="flex-1 flex items-center justify-center overflow-hidden min-w-0">
      {Icon && (
        <div className="w-24 max-w-full aspect-square">
          <Icon className="text-app-gray-900 w-full h-full" strokeWidth={2} />
        </div>
      )}
    </div>
  </div>
);

export const RestrictedPdfCard = ({ file, onReplace, onDelete }) => (
  <ErrorCard icon={FileLock} title="Restricted PDF" onReplace={onReplace} onDelete={onDelete}>
    <p>This document is restricted and can't be opened. Please remove the security restrictions and re-upload.</p>
  </ErrorCard>
);

export const UnsupportedFormatCard = ({ file, errorSubtype, onReplace, onDelete }) => (
  <ErrorCard icon={FileWarning} title="Unsupported File Format" onReplace={onReplace} onDelete={onDelete}>
    {errorSubtype === 'unconvertible' ? (
      <p>Consider replacing this file with a placeholder document to be stamped and indexed.</p>
    ) : (
      <p>Please upload in a supported format: .pdf, .jpg, .jpeg, .png, .gif, .webp, .doc, .docx, .xls, .xlsx, .ppt, .pptx, .eml, .msg, .txt, .rtf</p>
    )}
  </ErrorCard>
);

export const FileTooLargeCard = ({ file, onReplace, onDelete }) => (
  <ErrorCard icon={FileWarning} title="File Too Large" onReplace={onReplace} onDelete={onDelete}>
    <p>This file exceeds the size limit. Please compress or split the document and re-upload.</p>
  </ErrorCard>
);

export const ProcessingFailedCard = ({ file, onReplace, onDelete }) => (
  <ErrorCard icon={FileX} title="Something Went Wrong" onReplace={onReplace} onDelete={onDelete}>
    <p>
      Please attempt to upload this file again. If the issue persists, please contact{' '}
      <a href="mailto:support@stampstack.io" className="underline">support@stampstack.io</a>.
    </p>
  </ErrorCard>
);

export default ErrorCard;
